export type DealerPortalCustomerJourneyStatus =
  | "FIRST_CONTACT"
  | "PRODUCT_VIEWING"
  | "NEGOTIATION"
  | "CLOSED"
  | "NOT_COMPLETED";

export type DealerPortalJourneyPriority = "LOW" | "MEDIUM" | "HIGH";

export type DealerPortalCustomerType = "PRIVATE" | "BUSINESS" | "DEALER";

export type DealerPortalVehicleStatus =
  | "TRADE_IN"
  | "INBOUND"
  | "PICKED_UP"
  | "DELIVERED"
  | "SOLD"
  | "TEST"
  | "IN_STOCK";

export interface DealerPortalCompany {
  id: string;
  name: string | null;
  orgNumber: string | null;
  vatNumber?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  logoPreviewMediaId?: string | null;
  logoFullSizeMediaId?: string | null;
}

export interface DealerPortalCustomer {
  id: string;
  companyId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  customerType: DealerPortalCustomerType | null;
  personalNumber: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  companyConnection: string | null;
  orgNumber?: string | null;
  vatNumber?: string | null;
  contactPersonName?: string | null;
  contactPersonEmail?: string | null;
  contactPersonPhone?: string | null;
  isBuyer: boolean;
  isSeller: boolean;
  createdAt: string;
  updatedAt: string;
  vehicleConnections?: DealerPortalCustomerVehicleConnection[];
  documents?: DealerPortalCustomerDocument[];
  historyEvents?: DealerPortalCustomerHistoryEvent[];
  notes?: DealerPortalCustomerNote[];
  journeys?: DealerPortalCustomerJourney[];
  /** Antal (från list-API) för visning i listan */
  _count?: { historyEvents?: number; notes?: number };
}

export interface DealerPortalCustomerVehicleConnection {
  id: string;
  customerId: string;
  vehicleId: string;
  journeyId: string | null;
  createdAt: string;
  vehicle?: DealerPortalVehicle;
  journey?: { id: string };
}

export interface DealerPortalCustomerDocument {
  id: string;
  customerId: string;
  createdAt: string;
  media?: { id: string; originalName: string; s3Key: string; mimeType: string } | null;
}

export interface DealerPortalCustomerHistoryEvent {
  id: string;
  customerId: string;
  journeyId: string | null;
  actorId: string | null;
  eventType: string;
  description: string | null;
  createdAt: string;
  actor?: { user?: { username?: string } } | null;
}

export interface DealerPortalCustomerNote {
  id: string;
  customerId: string;
  actorId: string;
  content: string;
  /** Om anteckningen kommer från en kundresas kommentar (visas med annan färg på kundkortet) */
  journeyId?: string | null;
  createdAt: string;
  actor?: { user?: { username?: string } } | null;
}

export interface DealerPortalVehicle {
  id: string;
  companyId: string;
  regNr: string;
  make: string;
  model: string;
  year: number;
  status: DealerPortalVehicleStatus | null;
  parkingSpot: string | null;
  variant?: string | null;
  condition?: string | null;
  mileage?: number | null;
  engine?: string | null;
  transmission?: string | null;
  bodyType?: string | null;
  color?: string | null;
  fuelType?: string | null;
  horsepower?: number | null;
  engineSize?: string | null;
  vin?: string | null;
  equipment?: string[];
  createdAt: string;
  updatedAt: string;
  documents?: DealerPortalVehicleDocument[];
  /** Servicedetaljer (datum, miltal, typ, anteckningar) */
  serviceDetails?: DealerPortalVehicleServiceDetail[];
  comments?: DealerPortalVehicleComment[];
  historyEvents?: DealerPortalVehicleHistoryEvent[];
}

export interface DealerPortalVehicleServiceDetail {
  id: string;
  vehicleId: string;
  date: string;
  mileage: number;
  serviceType: string;
  notes?: string | null;
  createdAt: string;
}

export interface DealerPortalVehicleHistoryEvent {
  id: string;
  vehicleId: string;
  journeyId: string | null;
  actorId: string | null;
  eventType: string;
  description: string | null;
  createdAt: string;
  actor?: { user?: { username?: string } } | null;
}

export interface DealerPortalVehicleDocument {
  id: string;
  vehicleId: string;
  imageCategory?: string | null;
  documentCategory?: string | null;
  /** Skadekommentar när imageCategory = DAMAGE */
  comment?: string | null;
  createdAt: string;
  /** Flera vid PREVIEW + FULL_SIZE; annars en. För lista använd preview, för lightbox fullsize. */
  media?: Array<{
    id: string;
    originalName: string;
    s3Key: string;
    mimeType: string;
    mediaSize?: "PREVIEW" | "FULL_SIZE" | null;
  }>;
}

export interface DealerPortalVehicleComment {
  id: string;
  vehicleId: string;
  actorId: string;
  content: string;
  createdAt: string;
  actor?: {
    user?: { username?: string; email?: string };
  };
}

/** Summary of a car test linked to a dealer portal vehicle (same regNr, same company). */
export interface DealerPortalVehicleCarTestSummary {
  id: string;
  status: string;
  submittedAt: string | null;
  createdAt: string;
  registrationNo: string | null;
  carTestInformation?: { make?: string | null; model?: string | null; year?: number | null } | null;
}

export interface DealerPortalColleague {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export interface DealerPortalJourneyComment {
  id: string;
  journeyId: string;
  actorId: string;
  content: string;
  createdAt: string;
  actor?: {
    user?: { firstName?: string; lastName?: string };
  };
}

export interface DealerPortalCustomerJourney {
  id: string;
  companyId: string;
  customerId: string;
  vehicleId: string | null;
  status: DealerPortalCustomerJourneyStatus;
  priority: DealerPortalJourneyPriority | null;
  assignedActorId: string | null;
  followUpNote: string | null;
  followUpAt: string | null;
  createdAt: string;
  updatedAt: string;
  customer: DealerPortalCustomer;
  vehicle: DealerPortalVehicle | null;
  assignedActor?: DealerPortalColleague | null;
  comments: DealerPortalCustomerJourneyComment[];
  events: Array<{
    id: string;
    eventType: string;
    description: string | null;
    createdAt: string;
    actor?: { user?: { firstName?: string; lastName?: string } } | null;
  }>;
}

export const JOURNEY_STATUS_LABELS: Record<DealerPortalCustomerJourneyStatus, string> = {
  FIRST_CONTACT: "Första kontakt",
  PRODUCT_VIEWING: "Produktvisning",
  NEGOTIATION: "Förhandling",
  CLOSED: "Avslut",
  NOT_COMPLETED: "Ej avslutade",
};

export const JOURNEY_PRIORITY_LABELS: Record<DealerPortalJourneyPriority, string> = {
  LOW: "Låg",
  MEDIUM: "Medium",
  HIGH: "Hög",
};

export const VEHICLE_STATUS_LABELS: Record<DealerPortalVehicleStatus, string> = {
  TRADE_IN: "Inbyte",
  INBOUND: "Inleverans",
  PICKED_UP: "Hämtad",
  DELIVERED: "Levererad",
  SOLD: "Såld",
  TEST: "Test",
  IN_STOCK: "Lager",
};

/** Exakt samma 7 statusar som prototypen. TEST först (för filtrering + default för nya). */
export const VEHICLE_STATUSES: DealerPortalVehicleStatus[] = [
  "TEST",
  "TRADE_IN",
  "INBOUND",
  "PICKED_UP",
  "DELIVERED",
  "SOLD",
  "IN_STOCK",
];
